import { useState, useEffect, useCallback, useRef } from 'react'
import type { Camera } from '../../types'
import { queryRecordings, parseCameraTs, type RecordingSegment } from '../../lib/cameraApi'
import { supabase } from '../../lib/supabase'

interface Props {
  camera: Camera
  onClose: () => void
}

function pad2(n: number) { return n.toString().padStart(2, '0') }

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function timeStr(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

export function PlaybackTimeline({ camera, onClose }: Props) {
  const [date, setDate] = useState(toDateInput(new Date()))
  const [segments, setSegments] = useState<RecordingSegment[]>([])
  const [loading, setLoading] = useState(false)
  const [playingUrl, setPlayingUrl] = useState<string | null>(null)
  const [playingSegment, setPlayingSegment] = useState<RecordingSegment | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [playError, setPlayError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Day boundaries
  const dayStart = new Date(`${date}T00:00:00`)
  const dayEnd = new Date(`${date}T23:59:59`)
  const dayMs = 24 * 60 * 60 * 1000

  // Fetch recording segments for the selected date
  const loadSegments = useCallback(async () => {
    setLoading(true)
    try {
      const segs = await queryRecordings(camera.id, dayStart, dayEnd)
      setSegments(segs)
    } catch {
      setSegments([])
    } finally {
      setLoading(false)
    }
  }, [camera.id, date]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSegments() }, [loadSegments])

  // Play a segment — download MP4 and play in video element
  const playSegment = useCallback(async (seg: RecordingSegment) => {
    if (downloading) return
    setDownloading(true)
    setPlayingSegment(seg)
    setPlayError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const url = `/api/camera/recordings?cameraId=${camera.id}&action=download&startTime=${seg.startTime}&endTime=${seg.endTime}`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (!res.ok) throw new Error('Download failed')

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)

      // Revoke previous
      if (playingUrl) URL.revokeObjectURL(playingUrl)
      setPlayingUrl(objectUrl)
    } catch (err) {
      setPlayingSegment(null)
      setPlayError(err instanceof Error ? err.message : 'Playback failed')
    } finally {
      setDownloading(false)
    }
  }, [camera.id, downloading, playingUrl])

  // Auto-play when URL changes
  useEffect(() => {
    if (playingUrl && videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }, [playingUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playingUrl) URL.revokeObjectURL(playingUrl)
    }
  }, [playingUrl])

  // Click on the timeline bar to play the segment at that position
  const handleTimelineClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    const clickMs = dayStart.getTime() + pct * dayMs

    // Find the segment that contains this time
    const seg = segments.find(s => {
      const sMs = parseCameraTs(s.startTime).getTime()
      const eMs = parseCameraTs(s.endTime).getTime()
      return clickMs >= sMs && clickMs <= eMs
    })

    if (seg) playSegment(seg)
  }, [segments, dayStart, dayMs, playSegment]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="absolute inset-0 bg-black flex flex-col z-10">
      {/* Video player */}
      <div className="flex-1 relative min-h-0">
        {playingUrl ? (
          <video
            ref={videoRef}
            src={playingUrl}
            controls
            className="w-full h-full object-contain bg-black"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm gap-2">
            {downloading ? 'Loading recording...'
              : playError ? (
                <>
                  <p className="text-error">{playError}</p>
                  <button
                    className="text-xs text-brand hover:underline"
                    onClick={() => setPlayError(null)}
                  >
                    Dismiss
                  </button>
                </>
              )
              : !loading && segments.length === 0 ? 'No recordings for this date'
              : 'Select a time on the timeline below'}
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded hover:bg-black/90 transition-colors"
        >
          Back to Live
        </button>

        {/* Current segment info */}
        {playingSegment && (
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {timeStr(parseCameraTs(playingSegment.startTime))} — {timeStr(parseCameraTs(playingSegment.endTime))}
          </div>
        )}
      </div>

      {/* Timeline controls */}
      <div className="bg-gray-900 px-3 py-2 space-y-1.5">
        {/* Date picker + status */}
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={date}
            onChange={(e) => {
              if (playingUrl) URL.revokeObjectURL(playingUrl)
              setDate(e.target.value)
              setPlayingSegment(null)
              setPlayingUrl(null)
              setPlayError(null)
            }}
            className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1"
          />
          <span className="text-[10px] text-gray-500">
            {loading ? 'Loading...' : `${segments.length} segment${segments.length !== 1 ? 's' : ''}`}
          </span>

          {/* Hour markers */}
          <div className="flex-1" />
          <span className="text-[10px] text-gray-600">00:00</span>
          <span className="text-[10px] text-gray-600">12:00</span>
          <span className="text-[10px] text-gray-600">24:00</span>
        </div>

        {/* Timeline bar */}
        <div
          className="relative h-6 bg-gray-800 rounded cursor-pointer overflow-hidden"
          onClick={handleTimelineClick}
        >
          {/* Hour grid lines */}
          {Array.from({ length: 24 }, (_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 border-l border-gray-700/50"
              style={{ left: `${(i / 24) * 100}%` }}
            />
          ))}

          {/* Recording segments */}
          {segments.map((seg, i) => {
            const sMs = parseCameraTs(seg.startTime).getTime()
            const eMs = parseCameraTs(seg.endTime).getTime()
            const left = ((sMs - dayStart.getTime()) / dayMs) * 100
            const width = ((eMs - sMs) / dayMs) * 100
            const isPlaying = playingSegment?.startTime === seg.startTime
            return (
              <div
                key={i}
                className={`absolute top-0.5 bottom-0.5 rounded-sm transition-colors ${
                  isPlaying ? 'bg-brand' : 'bg-success/60 hover:bg-success/80'
                }`}
                style={{ left: `${Math.max(0, left)}%`, width: `${Math.max(0.2, width)}%` }}
                title={`${timeStr(parseCameraTs(seg.startTime))} — ${timeStr(parseCameraTs(seg.endTime))}`}
              />
            )
          })}

          {/* Now indicator (if today) */}
          {date === toDateInput(new Date()) && (
            <div
              className="absolute top-0 bottom-0 w-px bg-error"
              style={{ left: `${((Date.now() - dayStart.getTime()) / dayMs) * 100}%` }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
